import type { Language } from '@/shared';
import type { ConfirmDonationRequest } from './resources/ConfirmDonation';
import type { ExecutePaymentRequest, PaymentResponse } from './resources/ExecutePayment';
import type { GetAdyenPaymentDetailsRequest, PaymentDetailsResponse } from './resources/GetAdyenPaymentDetails';
import type { GetCorporateCampaignResponse, GetCorporateResponse } from './resources/GetCorporate';
import type { GetCorporateSeoResponse } from './resources/GetCorporateSeo';
import type { GetPaymentMethodsRequest, PaymentMethodsResponse } from './resources/GetPaymentMethods';
import type { PreRegisterDonationRequest, PreRegisterDonationResponse } from './resources/PreRegisterDonation';
import type { ExchangeRate } from '@/modules/config.interface';

const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);

const url = process.env.NEXT_PUBLIC_API_URL;

export class ApiService {
	private async request<T>(url: string, method?: 'POST' | 'PUT', body?: unknown) {
		return fetch(url, {
			method,
			mode: 'cors',
			body: body !== undefined ? JSON.stringify(body) : undefined,
		}).then(async (response) => {
			if (response.ok) return response.json();
			console.log(`Error in API request: ${url}`, response);
			throw await response.json();
		}) as Promise<T>;
	}

	public async getCorporateSeo(language: Language, corporateId: string, campaignId?: string) {
		let path = `${url}/v3/corporate/seo?language=${language}&corporateId=${corporateId}`;
		if (campaignId && isUUID(campaignId)) {
			path = path.concat(`&campaignId=${campaignId}`);
		}

		return this.request<GetCorporateSeoResponse>(path);
	}

	public async getCorporate(corporateId: string, language: Language) {
		return this.request<GetCorporateResponse>(
			`${url}/v3/corporate?corporateId=${corporateId}&languageCode=${language}`,
		);
	}

	public async getCorporateCampaign(corporateId: string, campaignId: string, language: Language) {
		return this.request<GetCorporateCampaignResponse>(
			`${url}/v3/corporate/campaign?corporateId=${corporateId}&campaignId=${campaignId}&languageCode=${language}`,
		);
	}

	public async getCurrencyRelation(currentCurrencyCode: string) {
		return this.request<{ relation: number }>(`${url}/v3/get-currency-relation?from=${currentCurrencyCode}&to=EUR`);
	}

	public async getExchangeRates() {
		return this.request<{ exchange: ExchangeRate }>('https://cdn.worldcoo.com/public/exchange.json');
	}

	public async preRegisterDonation(body: PreRegisterDonationRequest) {
		return this.request<PreRegisterDonationResponse>(`${url}/preregister-donation`, 'POST', body);
	}

	public async getPaymentMethods(body: GetPaymentMethodsRequest) {
		return this.request<PaymentMethodsResponse>(`${url}/v3/adyen-payment-methods`, 'POST', body).catch((error) => {
			window.alert(error.message);
			throw error;
		});
	}

	public async executePayment(body: ExecutePaymentRequest) {
		return this.request<PaymentResponse>(`${url}/v3/execute-adyen-payment`, 'POST', body).catch((error) => {
			window.alert(error.message);
			throw error;
		});
	}

	public async getAdyenPaymentDetails(body: GetAdyenPaymentDetailsRequest) {
		return this.request<PaymentDetailsResponse>(`${url}/v3/adyen-payment-details`, 'POST', body).catch((error) => {
			window.alert(error.message);
			throw error;
		});
	}

	public async confirmDonation(body: ConfirmDonationRequest, donationId: string) {
		return this.request(`${url}/donation/${donationId}/confirm`, 'POST', body);
	}
}
